import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent, type ReactElement } from 'react';
import { ZEIT_FORMAT_NAME, bereichAus, naechsteZeit, naechstesZeitFormat, nextCut, stepFrames, timelineDuration } from '@schnitt/shared';
import { useEditor } from '../store';
import { taktZeit } from '../takt';
import { tonFreischalten } from '../ton';
import { lageNachZug, setTransportLage, useTransportLage } from '../transportlage';
import { Icon } from './Icon';
import { useZeitText } from '../zeit';

/**
 * Play, and moving through the cut: a frame at a time, or cut to cut. Nothing
 * else lives here any more -- splitting and deleting belong to the selected
 * clip and sit in the toolbar, zooming is a pinch. That is what makes the
 * row fit a phone with room to spare.
 */
export function Transport(): ReactElement {
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead);
  const zeit = useZeitText();
  const zeitformat = useEditor((s) => s.zeitformat);
  const zeitformatWeiter = useEditor((s) => s.zeitformatWeiter);
  // 7.62: wo die Leiste steht, und der Zug, der sie umsetzt. Am Griff
  // ziehen, loslassen -- sie rastet am näheren Rand der Bühne ein.
  const lage = useTransportLage();
  const bereich = useEditor((s) => s.bereich);
  const setBereich = useEditor((s) => s.setBereich);
  const toast = useEditor((s) => s.toast);
  /**
   * Anfang oder Ende des Bereichs auf den Abspielkopf.
   *
   * Dieselbe Rechnung wie im Menü der Zeitleiste und bei I/O -- `bereichAus`
   * hält sie an einer Stelle, damit die drei Wege nicht auseinanderlaufen.
   */
  const setzeBereich = (was: 'von' | 'bis') => {
    const ende = timelineDuration(timeline);
    const fps = Math.max(1, timeline.settings.fps);
    const neu =
      was === 'von'
        ? bereichAus(playhead, bereich?.bis ?? ende, ende, 1 / fps)
        : bereichAus(bereich?.von ?? 0, playhead, ende, 1 / fps);
    if (!neu) {
      toast('info', was === 'von' ? 'Dahinter ist nichts mehr.' : 'Davor ist nichts.');
      return;
    }
    setBereich(neu);
    toast('success', `Bereich ${zeit(neu.von)} bis ${zeit(neu.bis)}.`);
  };
  const leiste = useRef<HTMLDivElement>(null);
  const zug = useRef<{ pointerId: number; startY: number; dy: number } | null>(null);
  const griffAn = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    zug.current = { pointerId: event.pointerId, startY: event.clientY, dy: 0 };
    leiste.current?.classList.add('zieht');
  };
  const griffBewegt = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const z = zug.current;
    const el = leiste.current;
    if (!z || z.pointerId !== event.pointerId || !el) return;
    const buehne = el.parentElement?.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    let dy = event.clientY - z.startY;
    if (buehne) {
      // Innerhalb der Bühne bleiben: die Leiste soll man nicht verlieren.
      const oben = buehne.top + 10 - (box.top - z.dy);
      const unten = buehne.bottom - 10 - (box.bottom - z.dy);
      dy = Math.max(oben, Math.min(unten, dy));
    }
    z.dy = dy;
    el.style.transform = `translateY(${dy}px)`;
  };
  const griffLos = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const z = zug.current;
    const el = leiste.current;
    if (!z || z.pointerId !== event.pointerId) return;
    zug.current = null;
    if (!el) return;
    const buehne = el.parentElement?.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    el.style.transform = '';
    el.classList.remove('zieht');
    if (buehne && Math.abs(z.dy) > 4) setTransportLage(lageNachZug(box.top + box.height / 2, buehne.top, buehne.bottom));
  };
  const playing = useEditor((s) => s.playing);
  const setPlaying = useEditor((s) => s.setPlaying);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const duration = timelineDuration(timeline);
  const fps = timeline.settings.fps;
  const lastFrame = useRef(0);

  // Advance the playhead in real time. rAF rather than setInterval so playback
  // stays in step with the display and pauses when the tab is hidden.
  //
  // Läuft ein Video, zählt aber nicht die Wanduhr, sondern dessen eigene Uhr
  // (siehe takt.ts): auf dem Telefon dekodiert das Gerät oft langsamer als
  // die Wanduhr läuft, und ein Kopf, der davoneilt, wurde vorher mit einem
  // Sprung eingefangen -- wovon das Bild jedes Mal stehen blieb.
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    lastFrame.current = performance.now();
    const tick = (now: number) => {
      const delta = (now - lastFrame.current) / 1000;
      lastFrame.current = now;
      // Der Takt kommt vom Bild, solange er plausibel ist -- eine Medienzeit,
      // die weit neben dem steht, was gerade laufen müsste, riss den Kopf
      // sonst mit und die Wiedergabe hielt sofort an.
      const next = naechsteZeit(useEditor.getState().playhead, delta, taktZeit());
      if (next >= duration) {
        useEditor.getState().setPlayhead(duration);
        setPlaying(false);
        return;
      }
      useEditor.getState().setPlayhead(next, false);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, duration, setPlaying]);

  const step = useCallback(
    (frames: number) => {
      // Stepping while it plays used to be pointless: playback wrote the
      // playhead again on the next frame and the step was gone.
      setPlaying(false);
      setPlayhead(stepFrames(useEditor.getState().playhead, frames, fps, duration));
    },
    [fps, duration, setPlayhead, setPlaying],
  );

  // Holding the button repeats, because nudging by twenty frames on a phone is
  // otherwise twenty taps.
  const repeat = useRef<{ delay: ReturnType<typeof setTimeout>; tick?: ReturnType<typeof setInterval> } | null>(null);
  const stopRepeat = useCallback(() => {
    if (!repeat.current) return;
    clearTimeout(repeat.current.delay);
    if (repeat.current.tick) clearInterval(repeat.current.tick);
    repeat.current = null;
  }, []);
  useEffect(() => stopRepeat, [stopRepeat]);

  const stepHandlers = (frames: number) => ({
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => {
      // Own the gesture, so a hold does not turn into a scroll or a long-press
      // menu on iOS.
      event.currentTarget.setPointerCapture(event.pointerId);
      step(frames);
      stopRepeat();
      repeat.current = {
        delay: setTimeout(() => {
          if (repeat.current) repeat.current.tick = setInterval(() => step(frames), 90);
        }, 420),
      };
    },
    onPointerUp: stopRepeat,
    onPointerCancel: stopRepeat,
    onLostPointerCapture: stopRepeat,
  });

  const jumpToCut = useCallback(
    (direction: 1 | -1) => {
      setPlaying(false);
      const target = nextCut(timeline, useEditor.getState().playhead, direction);
      if (target === null) {
        useEditor.getState().toast('info', direction === 1 ? 'Kein Schnitt danach.' : 'Kein Schnitt davor.');
        return;
      }
      setPlayhead(target);
    },
    [timeline, setPlayhead, setPlaying],
  );

  return (
    <div className={`transport ${lage === 'oben' ? 'oben' : ''}`} ref={leiste} data-transport-lage={lage}>
      <button
        className="transport-griff"
        aria-label="Leiste verschieben"
        title="Ziehen: nach oben oder unten. Doppelt tippen: zurück nach unten."
        data-transport-griff
        onPointerDown={griffAn}
        onPointerMove={griffBewegt}
        onPointerUp={griffLos}
        onPointerCancel={griffLos}
        onDoubleClick={() => setTransportLage('unten')}
      >
        <span aria-hidden="true">⋮</span>
      </button>
      {/*
        Der Umschalter für das Zeitformat (S31). Er sitzt an der Anzeige selbst
        und nicht in einem Blatt: wer das Format wechseln will, sieht dabei auf
        die Zahl, und ein Weg über zwei Menüs wäre für etwas, das man mehrmals
        am Tag anfasst, zu weit. Ein Knopf, eine Richtung, im Kreis.
      */}
      <button
        type="button"
        className="timecode"
        data-zeitformat={zeitformat}
        onClick={zeitformatWeiter}
        title={`Zeitformat: ${ZEIT_FORMAT_NAME[zeitformat]} — tippen für ${ZEIT_FORMAT_NAME[naechstesZeitFormat(zeitformat)]}`}
        aria-label={`Zeitformat ${ZEIT_FORMAT_NAME[zeitformat]}, tippen für ${ZEIT_FORMAT_NAME[naechstesZeitFormat(zeitformat)]}`}
      >
        <span className="now">{zeit(playhead, true)}</span>
        <span className="total">{zeit(duration)}</span>
      </button>
      <div className="nav">
        <button onClick={() => jumpToCut(-1)} aria-label="Zum vorigen Schnitt">
          <Icon name="skipBack" />
        </button>
        <button {...stepHandlers(-1)} aria-label="Ein Bild zurück">
          <Icon name="stepBack" />
        </button>
        <button
          className="play-btn"
          onClick={() => {
            // Play from the top again when it has run off the end; a play
            // button that does nothing there feels broken.
            if (!playing && duration > 0 && playhead >= duration) setPlayhead(0);
            // Noch in der Berührung: die Tonelemente freischalten. Auf dem
            // iPhone ist das der einzige Augenblick, in dem sie es dürfen
            // (7.37, siehe ton.ts) -- und zwar vor setPlaying, weil danach
            // nichts mehr zur Berührung gehört.
            if (!playing) tonFreischalten();
            setPlaying(!playing);
          }}
          disabled={duration === 0}
          aria-label={playing ? 'Pause' : 'Abspielen'}
        >
          <Icon name={playing ? 'pause' : 'play'} size={24} />
        </button>
        <button {...stepHandlers(1)} aria-label="Ein Bild vor">
          <Icon name="stepForward" />
        </button>
        <button onClick={() => jumpToCut(1)} aria-label="Zum nächsten Schnitt">
          <Icon name="skipForward" />
        </button>
      </div>
      {/* Rechts, wo bis zum 18. September nur Platzhalter stand: der Bereich.
          Sascha: „Bereiche müssen besser erreichbar sein.“ Er lag bis dahin
          allein im Menü der Zeitleiste -- ein langer Druck auf einen Streifen,
          von dem man wissen musste, dass er eins hat.
          `[` und `]` sind die Zeichen, die jedes Schnittprogramm dafür
          benutzt, und die Tasten I und O tun dasselbe. Der Platz bleibt so
          breit wie die Zeit links, damit der Abspielknopf unter der Mitte des
          Bildes sitzt. */}
      <div className="bereich-knoepfe" data-bereich-knoepfe={bereich ? `${bereich.von.toFixed(2)}-${bereich.bis.toFixed(2)}` : ''}>
        <button
          type="button"
          data-bereich-von
          aria-label="Bereich beginnt hier (I)"
          title="Bereich beginnt hier (I)"
          onClick={() => setzeBereich('von')}
        >
          [
        </button>
        <button
          type="button"
          data-bereich-bis
          aria-label="Bereich endet hier (O)"
          title="Bereich endet hier (O)"
          onClick={() => setzeBereich('bis')}
        >
          ]
        </button>
        {bereich ? (
          <button
            type="button"
            data-bereich-weg
            aria-label="Bereich aufheben"
            title={`Bereich ${zeit(bereich.von)} bis ${zeit(bereich.bis)} — tippen hebt ihn auf`}
            onClick={() => {
              setBereich(null);
              toast('info', 'Bereich aufgehoben.');
            }}
          >
            ×
          </button>
        ) : null}
      </div>
    </div>
  );
}
